/**
 * One step of the folding guide, drawn with the card's own pictures: the paper
 * exactly as the fold engine moves it (src/template/fold-guide.js), at any
 * moment of the step.
 *
 * `progress` runs from 0 to 1 through the step's phases. A flap folding over
 * is drawn as it looks from above while it turns about its crease - squashed
 * towards the crease, edge-on half way, then mirrored on the far side showing
 * its other face - so the animation is the real motion, not a cross-fade.
 *
 * At progress 0 the step is marked up the way a folding diagram is: the fold
 * line with the number printed beside it on the sheet, and an arrow from the
 * flap to where it lands.
 */
import { useMemo } from 'react';
import {
  apply, compose, partShift, toSvg, type Affine, type FoldGuide, type FoldPhase, type PaperStyle, type Pt, type TemplateDocument,
  type ZoneSymbol, type ZoneText,
} from '@foldmycard/scene';
import { BrandAt, BrandMark, brandOnSheet, isBrandView } from './brand.tsx';
import { MonoFilter, NoteText, useLayouts, usePlacements, type ShownImage } from './card-view.tsx';
import { hasPaper, PaperDefs, PaperFill } from './paper.tsx';
import { SymbolMark } from './symbol.tsx';

export type FoldStep = FoldGuide['steps'][number];

const pts = (p: Pt[]) => p.map((q) => `${q[0]},${q[1]}`).join(' ');
const det = (m: Affine) => m[0] * m[3] - m[1] * m[2];
let nextId = 0;

/** Seconds: a fold, a turn on the table, a collapse (many creases at once), and the rest between two moves. */
const SECONDS = { fold: 1.8, rotate: 1.2, collapse: 2.6, rest: 0.4 };

/** How long a step takes to play, in seconds. */
export function stepSeconds(step: FoldStep): number {
  return step.phases.reduce((s, ph) => s + SECONDS[ph.kind], 0) + SECONDS.rest * Math.max(0, step.phases.length - 1);
}

const ease = (u: number) => (u < 0.5 ? 4 * u * u * u : 1 - (-2 * u + 2) ** 3 / 2);

/** Which phase `progress` falls in, and how far through it (0..1). */
function locate(step: FoldStep, progress: number): { k: number; u: number } {
  let t = Math.min(1, Math.max(0, progress)) * stepSeconds(step);
  for (let k = 0; k < step.phases.length; k++) {
    const d = SECONDS[step.phases[k].kind];
    if (t <= d || k === step.phases.length - 1) return { k, u: Math.min(1, t / d) };
    t -= d;
    if (t <= SECONDS.rest) return { k, u: 1 };
    t -= SECONDS.rest;
  }
  return { k: 0, u: 0 };
}

/** A flap turned by angle t about `line`, seen from above: squashed by cos t towards the line. */
function squash(line: { p: Pt; n: Pt }, c: number): Affine {
  const [nx, ny] = line.n;
  const k = 1 - c;
  const o = nx * line.p[0] + ny * line.p[1];
  return [1 - k * nx * nx, -k * nx * ny, -k * nx * ny, 1 - k * ny * ny, k * o * nx, k * o * ny];
}

function rotation([cx, cy]: Pt, deg: number): Affine {
  const a = (deg * Math.PI) / 180, c = Math.cos(a), s = Math.sin(a);
  return [c, s, -s, c, cx - c * cx + s * cy, cy - s * cx - c * cy];
}

interface Pose {
  /** facet id -> sheet -> view, now */
  mats: Map<number, Affine>;
  /** back to front, now */
  order: number[];
  /** how far each lifted facet is off the table: sin of its turn */
  lift: Map<number, number>;
  k: number;
  u: number;
}

/**
 * The layer order before a fold phase, from the order after it: a fold turns its layers over,
 * so their order among themselves reverses. A swing from inside (the banger's pocket) and the
 * other kinds of phase leave the order as it is.
 */
function unfold(order: number[], ph: FoldPhase): number[] {
  if (ph.kind !== 'fold' || ph.inside) return order;
  const moving = new Set(ph.moving);
  const flipped = order.filter((id) => moving.has(id)).reverse();
  let i = 0;
  return order.map((id) => (moving.has(id) ? flipped[i++] : id));
}

/** Where every facet of the step is at `progress`. */
export function poseAt(step: FoldStep, progress: number): Pose {
  const last = step.phases.length;
  const endOrder = step.facets.map((f) => f.id);
  // a step with nothing to move (creasing the pattern first) is shown as it is
  if (progress >= 1 || !last) {
    return { mats: new Map(step.facets.map((f) => [f.id, f.at[last]])), order: endOrder, lift: new Map(), k: last - 1, u: 1 };
  }
  const { k, u } = locate(step, progress);
  const ph = step.phases[k];
  const e = ease(u);
  const mats = new Map<number, Affine>();
  const lift = new Map<number, number>();
  const moving = new Set(ph.kind === 'fold' ? ph.moving : []);
  for (const f of step.facets) {
    let m = f.at[k];
    if (ph.kind === 'fold' && moving.has(f.id)) {
      const turn = Math.PI * e;
      m = compose(squash(ph.line, Math.cos(turn)), m);
      lift.set(f.id, Math.sin(turn));
    } else if (ph.kind === 'rotate') {
      m = compose(rotation(ph.center, ph.deg * e), m);
    } else if (ph.kind === 'collapse') {
      // every facet at once, straight from where it lies to where it lands
      const to = f.at[k + 1];
      m = m.map((v, i) => v + (to[i] - v) * e) as Affine;
    }
    mats.set(f.id, m);
  }
  // The order at the end of THIS phase: the step's end order with every later phase undone. In a
  // step of several moves (fold and open again; fold two flaps) the end order is the last move's,
  // and drawing an earlier move in it showed the wrong layer on top (the banger's steps 4 and 5,
  // 2026-10-25: its inside showed through while the first flap turned).
  let after = endOrder;
  for (let j = last - 1; j > k; j--) after = unfold(after, step.phases[j]);
  // Until a flap passes upright it is still lifting off its own side: on top
  // of everything, its layers in the order they had before it turned over.
  // Paper swinging out from inside (the banger's pocket) stays behind all the way.
  const order = ph.kind === 'fold' && e < 0.5 && !ph.inside
    ? [...after.filter((id) => !moving.has(id)), ...after.filter((id) => moving.has(id)).reverse()]
    : after;
  return { mats, order, lift, k, u };
}

/**
 * One printed side's pictures, notes and symbols, on its own page (mm), to be placed on every facet
 * that shows it - and the FoldMyCard mark, printed on every card, as the print sheet has it
 * (card-view.tsx SheetFace; missing here until 2026-10-31, when the owner saw the square letter's
 * front fold up without it).
 */
function FaceArt({ id, template, face, pictures, notes, symbols, placements, layouts, paper, mono, plain }: {
  id: string; template: TemplateDocument; face: 1 | 2;
  pictures: Map<number, ShownImage>; notes?: Map<number, ZoneText>; symbols?: Map<number, ZoneSymbol>;
  placements: Map<number, Affine>; layouts: ReturnType<typeof useLayouts>; paper?: PaperStyle; mono?: boolean;
  plain?: Set<number>;
}) {
  const zones = template.zones.filter((z) => z.printFace === face).sort((a, b) => a.drawOrder - b.drawOrder);
  const grey = mono ? `url(#${id}-mono)` : undefined;
  const brand = useMemo(() => brandOnSheet(template), [template]);
  const backView = template.views.find((v) => isBrandView(template, v.id));
  return (
    <g id={`${id}-art${face}`}>
      {/* the paper, around the shapes - as printed (card-view.tsx SheetFace) */}
      {hasPaper(paper) && (
        <g mask={`url(#${id}-pm${face})`} filter={grey}>
          <PaperFill paper={paper} id={`${id}-pp`} W={template.paper.widthMm} H={template.paper.heightMm} />
        </g>
      )}
      {zones.map((z) => {
        const img = pictures.get(z.number);
        const place = placements.get(z.number);
        const layout = layouts.get(z.number);
        const note = notes?.get(z.number);
        return (
          <g key={z.number} clipPath={`url(#${id}-z${z.number})`}>
            {img && place && z.shifts ? (
              // a picture printed in pieces: each piece prints its own part of the whole
              [z.outline, ...z.extraOutlines].map((_, k) => (
                <g key={k} clipPath={`url(#${id}-z${z.number}-p${k})`}>
                  <image href={img.href} width={img.width} height={img.height} preserveAspectRatio="none" transform={toSvg(compose(partShift(z, k), place))} filter={grey} />
                </g>
              ))
            ) : img && place ? (
              <image href={img.href} width={img.width} height={img.height} preserveAspectRatio="none" transform={toSvg(place)} filter={grey} />
            ) : plain?.has(z.number) || layouts.has(z.number) || symbols?.has(z.number) ? null : (
              // attributes, not a class: this is drawn through <use>, where page styles may not reach
              [z.outline, ...z.extraOutlines].map((o, i) => <polygon key={i} points={pts(o)} fill="#0b1520" fillOpacity={0.05} />)
            )}
            {layout && note && (
              <g transform={toSvg(layout.toPage)} filter={grey}><NoteText layout={layout} note={note} veil={!!img} /></g>
            )}
            {symbols?.get(z.number) && <g filter={grey}><SymbolMark zone={z} symbol={symbols.get(z.number)!} /></g>}
          </g>
        );
      })}
      {brand && brand.face === face && backView && (
        <g transform={toSvg(brand.viewToPage)}><BrandMark w={backView.widthMm} h={backView.heightMm} /></g>
      )}
      {template.brandSpot?.face === face && <BrandAt spot={template.brandSpot} />}
    </g>
  );
}

/* ------------------------------------------------------------------ marks */

function Arrowhead({ at, from, size }: { at: Pt; from: Pt; size: number }) {
  const d: Pt = [at[0] - from[0], at[1] - from[1]];
  const L = Math.hypot(d[0], d[1]) || 1;
  const u: Pt = [d[0] / L, d[1] / L], v: Pt = [-u[1], u[0]];
  const back: Pt = [at[0] - u[0] * size, at[1] - u[1] * size];
  return (
    <polygon className="fold-arrowhead"
      points={pts([at, [back[0] + v[0] * size * 0.5, back[1] + v[1] * size * 0.5], [back[0] - v[0] * size * 0.5, back[1] - v[1] * size * 0.5]])} />
  );
}

/**
 * The fold line's number, as printed beside it on the sheet: just beyond the
 * top end of the line - or its other end, where two fold lines start from the
 * same point (the two corners of step 1 meet at the top).
 */
function LineBadge({ seg, label, s, avoid }: { seg: [Pt, Pt]; label: string; s: number; avoid: Pt[] }) {
  const top = seg[0][1] <= seg[1][1] ? 0 : 1;
  const crowded = avoid.some((p) => Math.hypot(p[0] - seg[top][0], p[1] - seg[top][1]) < 4 * s);
  const a = seg[crowded ? 1 - top : top], b = seg[crowded ? top : 1 - top];
  const L = Math.hypot(a[0] - b[0], a[1] - b[1]) || 1;
  const out: Pt = [(a[0] - b[0]) / L, (a[1] - b[1]) / L];
  const at: Pt = [a[0] + out[0] * 5 * s, a[1] + out[1] * 5 * s];
  const w = Math.max(6.4, label.length * 2.6) * s;
  return (
    <g className="fold-badge">
      <rect x={at[0] - w / 2} y={at[1] - 3.2 * s} width={w} height={6.4 * s} rx={3.2 * s} />
      <text x={at[0]} y={at[1]} fontSize={4 * s} textAnchor="middle" dominantBaseline="central">{label}</text>
    </g>
  );
}

function PhaseMarks({ ph, s, arrows, avoid }: { ph: FoldPhase; s: number; arrows: boolean; avoid: Pt[] }) {
  if (ph.kind === 'rotate') {
    // an arc round the centre, the way the sheet turns
    const r = 14 * s, a0 = -Math.PI / 2 - (Math.sign(ph.deg) * Math.PI) / 5;
    const a1 = a0 + (ph.deg * Math.PI) / 180 * 1.6;
    const p0: Pt = [ph.center[0] + r * Math.cos(a0), ph.center[1] + r * Math.sin(a0)];
    const p1: Pt = [ph.center[0] + r * Math.cos(a1), ph.center[1] + r * Math.sin(a1)];
    // a point just behind the end of the arc, along the way it travels
    const dir = Math.sign(ph.deg);
    const behind: Pt = [p1[0] + dir * Math.sin(a1), p1[1] - dir * Math.cos(a1)];
    return (
      <g className="fold-marks">
        <path d={`M${p0} A${r} ${r} 0 0 ${ph.deg > 0 ? 1 : 0} ${p1}`} className="fold-arrow" vectorEffect="non-scaling-stroke" />
        <Arrowhead at={p1} from={behind} size={3.4 * s} />
      </g>
    );
  }
  // a collapse has no one line to mark: the pieces moving show it
  if (ph.kind === 'collapse') return null;
  const [a, b] = ph.seg;
  if (ph.turn) {
    // turn the sheet over: an arc across its upright axis, over the top
    const top: Pt = a[1] < b[1] ? a : b;
    const span = 22 * s;
    const p0: Pt = [top[0] - span, top[1] + 4 * s], p1: Pt = [top[0] + span, top[1] + 4 * s];
    const via: Pt = [top[0], top[1] - 14 * s];
    return (
      <g className="fold-marks">
        <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} className="fold-axis" vectorEffect="non-scaling-stroke" />
        {arrows && (
          <>
            <path d={`M${p0} Q${via} ${p1}`} className="fold-arrow" vectorEffect="non-scaling-stroke" />
            <Arrowhead at={p1} from={via} size={3.4 * s} />
          </>
        )}
      </g>
    );
  }
  return (
    <g className="fold-marks">
      <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} className="fold-line" vectorEffect="non-scaling-stroke" />
      {arrows && ph.arrow && (
        <>
          <path d={`M${ph.arrow[0]} Q${ph.arrow[1]} ${ph.arrow[2]}`} className="fold-arrow" vectorEffect="non-scaling-stroke" />
          <circle cx={ph.arrow[0][0]} cy={ph.arrow[0][1]} r={1.3 * s} className="fold-arrow-start" />
          <Arrowhead at={ph.arrow[2]} from={ph.arrow[1]} size={3.4 * s} />
        </>
      )}
      {ph.label && <LineBadge seg={ph.seg} label={ph.label} s={s} avoid={avoid} />}
    </g>
  );
}

/* ------------------------------------------------------------------ stage */

export interface FoldStageProps {
  template: TemplateDocument;
  step: FoldStep;
  pictures: Map<number, ShownImage>;
  notes?: Map<number, ZoneText>;
  /** The symbols in symbol frames (symbol.tsx). */
  symbols?: Map<number, ZoneSymbol>;
  /** 0 = before the step, 1 = done. */
  progress: number;
  /** Draw the fold lines and arrows (at rest before the step, and while it moves). */
  marks?: boolean;
  label?: string;
  /**
   * 'step' frames the whole step, so nothing moves out of view while it plays;
   * 'tight' frames only what is drawn now - for a still picture.
   */
  fit?: 'step' | 'tight';
  /** The paper around the pictures (paper.tsx); absent is white. */
  paper?: PaperStyle;
  /** The whole card in black and white (ProjectDocument.mono). */
  mono?: boolean;
  /** The zones left plain (ZoneSlot.plain). */
  plain?: Set<number>;
}

export function FoldStage({ template, step, pictures, notes, symbols, progress, marks = true, label, fit = 'step', paper, mono, plain }: FoldStageProps) {
  const id = useMemo(() => `fs${++nextId}`, []);
  const placements = usePlacements(template, pictures);
  const layouts = useLayouts(template, notes);
  const W = template.paper.widthMm;
  const byId = useMemo(() => new Map(step.facets.map((f) => [f.id, f])), [step]);
  const pose = poseAt(step, progress);
  const zones = template.zones;

  // before the step: every fold of it, with its arrow (a turn on the table only
  // shows while it happens); while it moves: the current fold line alone
  // (a collapse has no line to show; a still step has nothing at all)
  const shown = progress >= 1 || !marks || !step.phases.length ? [] : progress === 0
    ? step.phases.filter((ph) => ph.kind === 'fold').map((ph) => ({ ph, arrows: true }))
    : step.phases[pose.k].kind === 'collapse' ? [] : [{ ph: step.phases[pose.k], arrows: false }];

  const box = fit === 'step' ? step.box : (() => {
    const p: Pt[] = step.facets.flatMap((f) => f.sheet.map((q) => apply(pose.mats.get(f.id)!, q)));
    for (const { ph } of shown) if (ph.kind === 'fold') p.push(...ph.seg, ...(ph.arrow || []));
    const xs = p.map((q) => q[0]), ys = p.map((q) => q[1]);
    const x0 = Math.min(...xs), y0 = Math.min(...ys), w = Math.max(...xs) - x0, h = Math.max(...ys) - y0;
    const pad = 0.08 * Math.max(w, h);
    // the turn-over arc rises above the sheet
    const over = shown.some(({ ph }) => ph.kind === 'fold' && ph.turn) ? 0.1 * Math.max(w, h) : 0;
    return [x0 - pad, y0 - pad - over, w + 2 * pad, h + 2 * pad + over];
  })();
  const s = Math.max(box[2], box[3]) / 200;       // half a percent of the view: sizes of marks

  return (
    <svg className="fold-svg" viewBox={box.join(' ')} role="img" aria-label={label || step.title}>
      <defs>
        {zones.map((z) => (
          <clipPath id={`${id}-z${z.number}`} key={z.number}>
            {[z.outline, ...z.extraOutlines].map((o, i) => <polygon key={i} points={pts(o)} />)}
          </clipPath>
        ))}
        {zones.filter((z) => z.shifts).flatMap((z) => [z.outline, ...z.extraOutlines].map((o, k) => (
          <clipPath id={`${id}-z${z.number}-p${k}`} key={`${z.number}-p${k}`}><polygon points={pts(o)} /></clipPath>
        )))}
        {step.facets.map((f) => (
          <clipPath id={`${id}-f${f.id}`} key={f.id}><polygon points={pts(f.sheet)} /></clipPath>
        ))}
        {mono && <MonoFilter id={`${id}-mono`} />}
        {hasPaper(paper) && (
          <>
            <PaperDefs paper={paper} id={`${id}-pp`} />
            {([1, 2] as const).map((face) => (
              <mask key={face} id={`${id}-pm${face}`} maskUnits="userSpaceOnUse" x={0} y={0} width={W} height={template.paper.heightMm}>
                <rect x={0} y={0} width={W} height={template.paper.heightMm} fill="#ffffff" />
                {zones.filter((z) => z.printFace === face && !plain?.has(z.number) && (pictures.has(z.number) || (!layouts.has(z.number) && !symbols?.has(z.number))))
                  .flatMap((z) => [z.outline, ...z.extraOutlines])
                  .map((o, i) => <polygon key={i} points={pts(o)} fill="#000000" />)}
              </mask>
            ))}
          </>
        )}
        {([1, 2] as const).map((face) => (
          <FaceArt key={face} id={id} template={template} face={face} pictures={pictures} notes={notes} symbols={symbols}
            placements={placements} layouts={layouts} paper={paper} mono={mono} plain={plain} />
        ))}
      </defs>
      {pose.order.map((fid) => {
        const f = byId.get(fid)!;
        const m = pose.mats.get(fid)!;
        const face = det(m) > 0 ? 1 : 2;
        const lift = pose.lift.get(fid) ?? 0;
        const sheet = pts(f.sheet);
        return (
          <g key={fid}>
            <polygon points={pts(f.sheet.map((p) => apply(m, p)))} className="fold-shadow"
              transform={`translate(${s * (0.6 + 4 * lift)} ${s * (0.9 + 6 * lift)})`} />
            <g transform={toSvg(m)}>
              <polygon points={sheet} className={`fold-paper side-${face}`} />
              <g clipPath={`url(#${id}-f${fid})`}>
                <use href={`#${id}-art${face}`} transform={face === 2 ? `matrix(-1 0 0 1 ${W} 0)` : undefined} />
              </g>
              {lift > 0.01 && <polygon points={sheet} className="fold-turning" fillOpacity={0.2 * lift} />}
              <polygon points={sheet} className="fold-edge" vectorEffect="non-scaling-stroke" />
            </g>
          </g>
        );
      })}
      {marks && progress < 1 && step.refs.map((r, i) => (
        <g key={`r${i}`} className="fold-marks">
          <line x1={r.seg[0][0]} y1={r.seg[0][1]} x2={r.seg[1][0]} y2={r.seg[1][1]} className="fold-ref" vectorEffect="non-scaling-stroke" />
        </g>
      ))}
      {shown.map(({ ph, arrows }, i) => (
        <PhaseMarks key={i} ph={ph} s={s} arrows={arrows}
          avoid={shown.flatMap((o, j) => (j !== i && o.ph.kind === 'fold' ? o.ph.seg : []))} />
      ))}
    </svg>
  );
}
